import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@puckeditor/core'
import { File as FileIcon, Loader2, Search, X } from 'lucide-react'
import {
  MEDIA_LIBRARY_TIMEOUT_MESSAGE,
  PUCK_LIBRARY_FOLDER,
  fetchMediaLibrary,
  isAbortError,
  type MediaImage,
} from '../../../services/mediaLibrary'
import * as styles from './FileUploadField.module.scss'

export const SEARCH_DEBOUNCE_MS = 300

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

const imageLabel = (image: MediaImage) =>
  image.name || image.filename || image.link.split('/').pop() || 'Untitled'

const isPreviewable = (image: MediaImage) =>
  image.contentType
    ? image.contentType.startsWith('image/')
    : /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(image.link)

/** The latest value once it has stopped changing for `delay` ms. */
const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

type LibraryQuery = { search: string; folder: string }

type LibraryState = {
  images: MediaImage[]
  page: number
  hasMore: boolean
  loading: boolean
  error: string | null
  /** The filters the shown images answer, for the empty-state wording. */
  query: LibraryQuery
}

const INITIAL_STATE: LibraryState = {
  images: [],
  page: 0,
  hasMore: false,
  loading: true,
  error: null,
  query: { search: '', folder: '' },
}

/**
 * What to tell the author when a page did not load: the API's own `message` when it
 * sent one, the timeout sentence when it did not answer (or could not be reached),
 * else the status.
 */
export const libraryErrorMessage = (error: unknown): string => {
  const apiMessage = (error as { body?: Record<string, unknown> })?.body
    ?.message
  if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage
  const status = (error as { status?: unknown })?.status
  if (typeof status === 'number') {
    return `The media library could not be loaded (${status}). Try again.`
  }
  const message = error instanceof Error ? error.message : ''
  return message || MEDIA_LIBRARY_TIMEOUT_MESSAGE
}

/** The empty-state sentence for the filters that produced no images. */
export const emptyLibraryMessage = ({ search }: LibraryQuery) => {
  const term = search.trim()
  return term ? `No images match "${term}".` : 'No images in the library yet.'
}

/**
 * Newest-first library pages for a search and folder. A new search replaces the list;
 * `loadMore` appends the next page. Answers to a superseded request are dropped, so a
 * slow earlier search never overwrites a newer one.
 */
const useLibrary = (search: string, folder: string) => {
  const [state, setState] = useState<LibraryState>(INITIAL_STATE)
  const requestId = useRef(0)
  // The request in flight. Aborted when a newer one replaces it or the picker closes:
  // it holds the editor's admin queue until it settles.
  const inFlight = useRef<AbortController | null>(null)

  const load = useCallback(
    async (page: number) => {
      const id = ++requestId.current
      inFlight.current?.abort()
      const controller = new AbortController()
      inFlight.current = controller
      setState(prev => ({
        ...prev,
        ...(page === 0 ? { images: [], hasMore: false } : {}),
        loading: true,
        error: null,
      }))
      try {
        const result = await fetchMediaLibrary(
          { search, folder, page },
          { signal: controller.signal },
        )
        if (id !== requestId.current) return
        setState(prev => {
          // An upload between two pages shifts the list by one, so the next page can
          // repeat the last image of the previous one.
          const seen = new Set(
            page === 0 ? [] : prev.images.map(image => image.id),
          )
          const fresh = result.images.filter(image => !seen.has(image.id))
          return {
            images: page === 0 ? fresh : [...prev.images, ...fresh],
            page: result.page,
            hasMore: result.hasMore,
            loading: false,
            error: null,
            query: { search, folder },
          }
        })
      } catch (err) {
        // Cancelled on purpose: a newer request or a closed picker owns the state.
        if (id !== requestId.current || isAbortError(err)) return
        setState(prev => ({
          ...prev,
          loading: false,
          error: libraryErrorMessage(err),
        }))
      } finally {
        if (inFlight.current === controller) inFlight.current = null
      }
    },
    [search, folder],
  )

  useEffect(() => {
    load(0)
  }, [load])

  // Drop whatever is still in flight once the picker closes, and cancel it so the
  // next opening is not queued behind it.
  useEffect(
    () => () => {
      requestId.current += 1
      inFlight.current?.abort()
    },
    [],
  )

  const loadMore = useCallback(() => load(state.page + 1), [load, state.page])
  const retry = useCallback(
    () => load(state.images.length ? state.page + 1 : 0),
    [load, state.images.length, state.page],
  )

  return { ...state, loadMore, retry }
}

export type MediaLibraryPickerProps = {
  /** Folder the picker searches; editor images live under /puck/. */
  folder?: string
  onSelect: (image: MediaImage) => void
  onClose: () => void
}

/**
 * "Choose from library" (BR-5691): a modal over the whole editor, since the Puck
 * sidebar is too narrow for a grid. It renders into document.body; React events still
 * bubble to the field, so keys are stopped here before they reach Puck's shortcuts or
 * the upload dropzone.
 */
export const MediaLibraryPicker: React.FC<MediaLibraryPickerProps> = ({
  folder = PUCK_LIBRARY_FOLDER,
  onSelect,
  onClose,
}) => {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput, SEARCH_DEBOUNCE_MS)
  const { images, hasMore, loading, error, query, loadMore, retry } =
    useLibrary(search, folder)

  // Infinite scroll, like the AI panel's /asset picker: load the next (older) page
  // when the end of the list comes near. Without IntersectionObserver a button stays.
  const bodyRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const canObserve = typeof IntersectionObserver !== 'undefined'
  const shouldLoadMore = hasMore && !loading && !error
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!canObserve || !shouldLoadMore || !sentinel) return
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { root: bodyRef.current, rootMargin: '300px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [canObserve, shouldLoadMore, loadMore])

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return
    // Keep Tab inside the dialog while it is open.
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    )
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const isEmpty = !loading && !error && images.length === 0

  return createPortal(
    <div
      className={styles.libraryOverlay}
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose()
      }}
      onClick={event => event.stopPropagation()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={styles.libraryDialog}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.libraryHeader}>
          <h2 id={titleId} className={styles.libraryTitle}>
            Choose from library
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={styles.libraryClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className={styles.libraryFilters}>
          <label className={styles.libraryFilter}>
            <span>Search</span>
            <span className={styles.librarySearch}>
              <Search size={14} aria-hidden />
              <input
                ref={searchRef}
                type="search"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Search images by name"
              />
            </span>
          </label>
        </div>

        <div ref={bodyRef} className={styles.libraryBody} aria-busy={loading}>
          {images.length > 0 && (
            <ul className={styles.libraryGrid}>
              {images.map(image => {
                const label = imageLabel(image)
                return (
                  <li key={image.id}>
                    <button
                      type="button"
                      className={styles.libraryTile}
                      onClick={() => onSelect(image)}
                      title={label}
                    >
                      <span className={styles.libraryThumb}>
                        {isPreviewable(image) ? (
                          <img src={image.link} alt="" loading="lazy" />
                        ) : (
                          <FileIcon size={28} aria-hidden />
                        )}
                      </span>
                      <span className={styles.libraryName}>{label}</span>
                      {image.width && image.height ? (
                        <span className={styles.librarySize}>
                          {image.width} × {image.height}
                        </span>
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {isEmpty && (
            <p className={styles.libraryMessage} role="status">
              {emptyLibraryMessage(query)}
            </p>
          )}

          {error && (
            <div className={styles.libraryMessage}>
              <p className={styles.error} role="alert">
                {error}
              </p>
              <Button type="button" variant="secondary" onClick={retry}>
                Try again
              </Button>
            </div>
          )}

          {loading && (
            <p className={styles.libraryMessage} role="status">
              <Loader2 size={16} className={styles.spinner} aria-hidden />
              Loading…
            </p>
          )}

          {shouldLoadMore &&
            (canObserve ? (
              <div
                ref={sentinelRef}
                className={styles.librarySentinel}
                aria-hidden
              />
            ) : (
              <div className={styles.libraryMore}>
                <Button type="button" variant="secondary" onClick={loadMore}>
                  Load more
                </Button>
              </div>
            ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
